// Copyright (c) StrangeBee 2023

import React, { createContext, ReactElement, ReactNode, SetStateAction, useContext, useState } from 'react';
import { StoragePins, useStoragePins } from './hook';

const LIMIT = 10;

export enum PinTypes {
    TASK = 'tasks',
    OBSERVABLE = 'observables',
}

export interface PinType {
    caseId: string;
    type: PinTypes;
    id: string;
    url: string;
    title: string;
}
export interface PinContextInterface {
    isPinned: (s: PinType | null) => boolean;
    getLimit: (s: Partial<PinType>) => number;
    togglePin: (s: PinType) => void;
    removeMultiPin: (s: PinType[]) => void;
    addMultiPin: (s: PinType[]) => void;
    getPinsCase: (caseId: string) => Record<string, PinType[]>;
    shortcutVisible: boolean;
    setShortcutVisible: React.Dispatch<SetStateAction<boolean>>;
}
export const PinContext = createContext<PinContextInterface>({
    shortcutVisible: false,
    isPinned: () => true,
    getLimit: () => LIMIT,
    togglePin: () => {
        //empty
    },
    removeMultiPin: () => {
        //empty
    },
    addMultiPin: () => {
        //empty
    },
    getPinsCase: () => ({}),
    setShortcutVisible: () => {
        //empty
    },
});

interface PinProviderProps {
    children?: ReactNode;
}

interface ExtractDataProps {
    casesPin: Record<string, PinType[]>;
    libPin: PinType[];
}

export const PinProvider = ({ children }: PinProviderProps): ReactElement => {
    const { pins, setPins } = useStoragePins();
    const [shortcutVisible, setShortcutVisible] = useState(false);

    const multiSetPin = (newState: StoragePins): void => {
        setPins((prevState) => ({ ...prevState, ...newState }));
    };

    const extractData = (caseId: string, type: string, state = pins): ExtractDataProps => {
        const casesPin = state?.[caseId] || {};
        const libPin = casesPin?.[type] || [];
        return { casesPin, libPin };
    };

    const savePin = (pinsTreated: PinType[]): void => {
        const newState = pinsTreated.reduce((acc, pin) => {
            const { caseId, type } = pin;
            const { casesPin, libPin } = extractData(caseId, type, acc);

            if (libPin.length < LIMIT) {
                return { ...acc, [caseId]: { ...casesPin, [type]: [...libPin, pin] } };
            }

            return acc;
        }, pins as StoragePins);

        multiSetPin(newState);
    };

    const removePin = (pinsTreated: PinType[]): void => {
        const newState = pinsTreated.reduce((acc, pin) => {
            const { caseId, type, id } = pin;
            const { casesPin, libPin } = extractData(caseId, type, acc);
            return { ...acc, [caseId]: { ...casesPin, [type]: [...libPin.filter(({ id: currId }) => id !== currId)] } };
        }, pins as StoragePins);
        multiSetPin(newState);
    };

    const getPinsCase = (caseId: string): Record<string, PinType[]> => pins?.[caseId] || {};
    const getLimit = (data: Partial<PinType>): number => {
        const { type = '-', caseId = '-' } = data;
        if (!pins[caseId]) return LIMIT;
        if (!pins[caseId][type]) return LIMIT;
        return LIMIT - pins[caseId][type].length;
    };

    const isPinned = (pin: PinType | null): boolean => {
        if (pin === null) return false;
        const { caseId, type, id } = pin;
        const { libPin } = extractData(caseId, type);

        return !!libPin.find(({ id: currId }) => id === currId);
    };

    const removeMultiPin = (pins: PinType[]): void => {
        removePin(pins.filter((pin) => isPinned(pin)));
    };
    const addMultiPin = (pins: PinType[]): void => {
        savePin(pins.filter((pin) => !isPinned(pin)));
    };

    const togglePin = (pin: PinType): void => {
        if (isPinned(pin)) {
            removePin([pin]);
        } else {
            savePin([pin]);
        }
    };

    return (
        <PinContext.Provider
            value={{
                togglePin,
                removeMultiPin,
                addMultiPin,
                getPinsCase,
                isPinned,
                getLimit,
                shortcutVisible,
                setShortcutVisible,
            }}
        >
            {children}
        </PinContext.Provider>
    );
};
export const usePin = (): PinContextInterface => {
    return useContext(PinContext);
};
